import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, X, PlayCircle, Loader2 } from "lucide-react";

// localStorage key — set once the learner closes the modal so we don't
// re-open it on every page visit. The Help → Walkthrough button still
// works manually after this is set.
const SEEN_KEY = "uvc_walkthrough_seen_v1";

/**
 * Modal that plays the auto-generated learner walkthrough.
 *
 * Usage:
 *   <WalkthroughModal open={open} onClose={...} />
 *
 * The video URL is fetched from `/api/walkthrough/status`. While the build
 * is still cooking on the server we show a friendly "coming soon" state
 * instead of a broken <video> tag.
 */
export default function WalkthroughModal({ open, onClose }) {
  const [status, setStatus] = useState(null); // {available, url, download_url, size_bytes}
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api
      .get("/walkthrough/status")
      .then(({ data }) => { if (!cancelled) setStatus(data); })
      .catch(() => { if (!cancelled) setStatus({ available: false }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  // Auto-pause on close so audio doesn't keep playing in the background.
  useEffect(() => {
    if (!open && videoRef.current) {
      try { videoRef.current.pause(); } catch { /* noop */ }
    }
  }, [open]);

  if (!open) return null;

  const handleClose = () => {
    try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* noop */ }
    onClose?.();
  };

  // Resolve absolute URLs on the API host so <video src> + the download
  // link work from anywhere the modal renders. The backend returns paths
  // already prefixed with `/api/...` so we just concat with REACT_APP_BACKEND_URL.
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const videoSrc = status?.url ? `${apiBase}${status.url}` : null;
  const downloadHref = status?.download_url ? `${apiBase}${status.download_url}` : null;

  return (
    <div
      data-testid="walkthrough-modal"
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 py-6 animate-in fade-in duration-200"
      onClick={handleClose}
    >
      <div
        className="relative bg-[#0e0e0e] text-white rounded-2xl border border-white/10 shadow-2xl max-w-4xl w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          data-testid="walkthrough-close"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 border border-white/15 text-white/80 hover:text-white flex items-center justify-center transition-colors"
          aria-label="Close walkthrough"
        >
          <X size={16} />
        </button>

        <div className="relative aspect-video bg-black flex items-center justify-center">
          {loading ? (
            <div className="text-white/70 flex flex-col items-center gap-2">
              <Loader2 size={28} className="animate-spin" />
              <span className="text-xs uppercase tracking-[0.25em]">Loading walkthrough…</span>
            </div>
          ) : status?.available && videoSrc ? (
            <video
              ref={videoRef}
              data-testid="walkthrough-video"
              src={videoSrc}
              controls
              autoPlay
              playsInline
              className="w-full h-full"
            />
          ) : (
            <div className="text-white/70 max-w-md text-center px-6">
              <PlayCircle size={42} className="mx-auto mb-3 text-[#E53935]" strokeWidth={1.4} />
              <div className="text-lg font-semibold mb-2">Walkthrough coming soon</div>
              <p className="text-sm text-white/60">
                Your admin is still preparing the learner walkthrough video. Check
                back in a few minutes.
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 bg-[#161616]">
          <div className="text-xs text-white/60">
            A quick tour of every feature you&apos;ll use as a learner — courses,
            reading, Aria, quizzes, certificates and more.
          </div>
          <div className="flex items-center gap-2">
            {downloadHref && status?.available && (
              <a
                data-testid="walkthrough-download"
                href={downloadHref}
                download="UniVicoustic-Learner-Walkthrough.mp4"
                className="inline-flex items-center gap-1.5 bg-[#E53935] hover:bg-[#c5302d] text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
              >
                <Download size={12} /> Download MP4
              </a>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleClose}
              data-testid="walkthrough-skip"
              className="bg-transparent border-white/20 text-white/80 hover:bg-white/10 hover:text-white text-xs"
            >
              Got it
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Helper hook: returns true once on first mount if the learner has never
 * dismissed the walkthrough modal. Pair with WalkthroughModal's `open` prop.
 */
export function useFirstVisitWalkthrough() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    try {
      const seen = localStorage.getItem(SEEN_KEY);
      if (!seen) {
        // Defer a beat so it doesn't fight the page's own mount animations.
        const t = setTimeout(() => setOpen(true), 1200);
        return () => clearTimeout(t);
      }
    } catch { /* localStorage unavailable */ }
  }, []);
  return [open, setOpen];
}
